import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Tooltip,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "../api";

// ══════════════════════════════════════════════════════════════
// CONSTANTS
// ══════════════════════════════════════════════════════════════

const PRIMARY = "#005E60";
const DARK = "#1a1a2e";

const COMPONENT_COLORS = {
  VFD: { bg: "#005E60", light: "#e6f4f4" },
  TRANS: { bg: "#007b7d", light: "#e6f5f5" },
  MOTOR: { bg: "#00843d", light: "#e6f5ec" },
  GEN: { bg: "#c47a00", light: "#fdf3e3" },
  SWBDS: { bg: "#5c35a0", light: "#f0eafa" },
  UPS: { bg: "#b00033", light: "#fce8ed" },
  DBR: { bg: "#0077b6", light: "#e6f2fb" },
};

const SPEC_SECTIONS = {
  VFD: {
    "Electrical Parameters": [
      "input_voltage",
      "output_voltage",
      "power_rating_kw",
      "input_frequency",
      "output_frequency",
      "dc_bus_voltage",
      "input_current_a",
      "output_current_a",
    ],
    Performance: [
      "efficiency_percent",
      "power_factor",
      "switching_frequency_khz",
      "overload_capacity",
      "harmonic_distortion_thd",
      "harmonic_filter",
    ],
    "Protection & Control": [
      "protection_functions",
      "braking_method",
      "communication_protocol",
    ],
    Physical: [
      "cooling_method",
      "enclosure_rating",
      "insulation_class",
      "mounting_arrangement",
    ],
    Standards: ["shock_standard", "emc_standard"],
  },
  TRANS: {
    "Electrical Parameters": [
      "primary_voltage",
      "secondary_voltage",
      "power_rating_kva",
      "frequency",
      "vector_group",
      "turns_ratio",
      "primary_current_a",
      "secondary_current_a",
    ],
    Performance: [
      "impedance_voltage_percent",
      "no_load_losses_w",
      "full_load_losses_w",
      "no_load_current_percent",
    ],
    Protection: [
      "fault_current_contribution_ka",
      "short_circuit_withstand_ka",
      "short_circuit_duration_s",
      "partial_discharge_pc",
    ],
    Physical: [
      "insulation_class",
      "temperature_rise_k",
      "cooling_type",
      "enclosure_rating",
      "tapping_range",
      "winding_configuration",
    ],
    Standards: ["shock_standard", "emc_standard"],
  },
  MOTOR: {
    "Electrical Parameters": [
      "rated_power_kw",
      "rated_voltage",
      "frequency",
      "rated_current_a",
      "starting_current_a",
      "power_factor",
    ],
    Performance: [
      "efficiency_percent",
      "efficiency_class",
      "rated_speed_rpm",
      "synchronous_speed_rpm",
      "number_of_poles",
      "service_factor",
      "duty_cycle",
    ],
    Mechanical: [
      "mounting_type",
      "frame_size",
      "bearing_type",
      "moment_of_inertia_kgm2",
      "locked_rotor_torque_percent",
      "breakdown_torque_percent",
    ],
    Physical: [
      "insulation_class",
      "temperature_rise_k",
      "cooling_method",
      "enclosure_rating",
      "starting_method",
    ],
    Standards: ["shock_standard", "emc_standard"],
  },
  GEN: {
    "Electrical Parameters": [
      "rated_power_kva",
      "rated_power_kw",
      "rated_voltage",
      "frequency",
      "power_factor",
      "rated_current_a",
      "speed_rpm",
      "number_of_poles",
    ],
    Performance: [
      "efficiency_percent",
      "voltage_regulation_percent",
      "transient_voltage_dip_percent",
      "frequency_regulation_percent",
    ],
    "Machine Parameters": [
      "subtransient_reactance_xd",
      "transient_reactance_xd",
      "synchronous_reactance_xd",
      "short_circuit_ratio",
      "fault_current_contribution_ka",
    ],
    "Excitation & Control": [
      "excitation_type",
      "avr_type",
      "parallel_operation",
    ],
    Physical: [
      "insulation_class",
      "temperature_rise_k",
      "cooling_method",
      "enclosure_rating",
      "mounting_arrangement",
    ],
    Standards: ["shock_standard", "emc_standard"],
  },
  SWBDS: {
    "Bus Parameters": [
      "bus_voltage",
      "bus_current_rating_a",
      "frequency",
      "busbar_material",
      "busbar_rating_a",
      "busbar_configuration",
    ],
    Protection: [
      "fault_level_ka",
      "fault_duration_s",
      "breaking_capacity_ka",
      "arc_flash_mitigation",
      "protection_relay_type",
      "protection_standard",
    ],
    Configuration: [
      "number_of_incoming_feeders",
      "incoming_breaker_rating_a",
      "number_of_outgoing_feeders",
      "form_of_separation",
      "neutral_earthing",
    ],
    "Metering & Comms": ["metering"],
    Physical: ["enclosure_rating", "cooling_method", "mounting_arrangement"],
    Standards: ["shock_standard", "emc_standard"],
  },
  UPS: {
    "Electrical Parameters": [
      "input_voltage",
      "output_voltage",
      "power_rating_kva",
      "power_rating_kw",
      "frequency",
      "input_frequency_range",
    ],
    Performance: [
      "efficiency_percent",
      "output_voltage_regulation",
      "output_frequency_regulation",
      "harmonic_distortion_thd",
      "power_factor_input",
      "power_factor_output",
      "overload_capacity",
    ],
    Battery: [
      "battery_type",
      "battery_voltage",
      "battery_autonomy_min",
      "battery_recharge_time_h",
    ],
    Bypass: [
      "topology",
      "static_bypass",
      "maintenance_bypass",
      "bypass_rating_a",
    ],
    Physical: [
      "insulation_class",
      "enclosure_rating",
      "cooling_method",
      "mounting_arrangement",
      "communication_protocol",
    ],
    Standards: ["shock_standard", "emc_standard"],
  },
  DBR: {
    "Electrical Parameters": [
      "dc_bus_voltage",
      "peak_braking_power_kw",
      "continuous_braking_power_kw",
      "peak_current_a",
      "continuous_current_a",
      "resistance_value_ohm",
      "resistance_tolerance",
    ],
    "Duty & Thermal": [
      "braking_duration_peak_s",
      "braking_duration_continuous",
      "duty_cycle",
      "energy_per_cycle_kwh",
      "thermal_trip_temperature_c",
      "thermal_trip_reset",
      "temperature_monitoring",
    ],
    Construction: [
      "number_of_resistor_banks",
      "resistor_element_material",
      "insulation_class",
    ],
    Physical: ["enclosure_rating", "cooling_method", "mounting_arrangement"],
    Connections: ["connected_vfd_tag", "connected_motor_tag"],
    Standards: ["shock_standard", "emc_standard"],
  },
};

// ── Dimension fields — shown in their own section ─────────────
const DIMENSION_FIELDS = new Set([
  "max_height_mm",
  "max_width_mm",
  "max_depth_mm",
  "max_weight_kg",
  "floor_loading_kn_m2",
]);

const DIMENSION_LABELS = {
  max_height_mm: "Max Height (mm)",
  max_width_mm: "Max Width (mm)",
  max_depth_mm: "Max Depth (mm)",
  max_weight_kg: "Max Weight (kg)",
  floor_loading_kn_m2: "Max Floor Loading (kN/m²)",
};

// ══════════════════════════════════════════════════════════════
// HELPERS
// ══════════════════════════════════════════════════════════════

const formatLabel = (key) =>
  DIMENSION_LABELS[key] ||
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// ══════════════════════════════════════════════════════════════
// SPEC SECTION CARD
// ══════════════════════════════════════════════════════════════

function SpecSectionCard({ title, fields, specs, color }) {
  const items = fields
    .filter((f) => specs[f])
    .map((f) => ({ key: f, value: specs[f] }));
  if (items.length === 0) return null;

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: "12px",
        border: "1px solid #e0e0e0",
        borderLeft: `4px solid ${color.bg}`,
        mb: "1rem",
      }}
    >
      <Box
        sx={{
          backgroundColor: color.light,
          padding: "0.6rem 1.25rem",
          borderRadius: "11px 11px 0 0",
        }}
      >
        <Typography
          fontWeight="bold"
          fontSize="0.85rem"
          sx={{ color: color.bg }}
        >
          {title}
        </Typography>
      </Box>
      <CardContent sx={{ pt: "1rem" }}>
        <Grid container spacing={2}>
          {items.map(({ key, value }) => (
            <Grid item xs={12} sm={6} md={4} key={key}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                {formatLabel(key)}
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {String(value)}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
}

// ══════════════════════════════════════════════════════════════
// VIEW ESS
// ══════════════════════════════════════════════════════════════

function ViewESS() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [ess, setEss] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [dlError, setDlError] = useState("");

  const projectId = location.state?.projectId || null;

  // ── Fetch ESS ─────────────────────────────────────────────
  const fetchEss = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/ess/${id}`);
      setEss(res.data);
    } catch {
      setError("Failed to load ESS document. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchEss();
  }, [fetchEss]);

  // ── Download Word ─────────────────────────────────────────
  const handleDownloadWord = async () => {
    setDlError("");
    try {
      const res = await api.get(`/ess/${id}/download`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `ESS_${ess.component_type}_${ess.tag_number || id}.docx`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setDlError("Failed to download Word document. Please try again.");
    }
  };

  // ── Delete ────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!window.confirm("Delete this ESS document? This cannot be undone."))
      return;
    setDeleting(true);
    try {
      await api.delete(`/ess/${id}`);
      navigate(projectId ? `/project/${projectId}` : "/");
    } catch {
      setError("Failed to delete ESS document.");
      setDeleting(false);
    }
  };

  // ── Loading state ─────────────────────────────────────────
  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", padding: "4rem" }}>
        <CircularProgress sx={{ color: PRIMARY }} />
      </Box>
    );

  if (error && !ess)
    return (
      <Box sx={{ mt: "2rem" }}>
        <Alert severity="error" sx={{ borderRadius: "8px", mb: "1rem" }}>
          {error}
        </Alert>
        <Button
          startIcon={<RefreshIcon />}
          onClick={fetchEss}
          sx={{ color: PRIMARY, textTransform: "none" }}
        >
          Try Again
        </Button>
      </Box>
    );

  const compType = ess.component_type?.toUpperCase();
  const color = COMPONENT_COLORS[compType] || { bg: PRIMARY, light: "#e6f4f4" };
  const sections = SPEC_SECTIONS[compType] || {};
  const specs = ess.specifications || {};

  // Fields already in defined sections
  const coveredFields = new Set([
    ...Object.values(sections).flat(),
    ...Array.from(DIMENSION_FIELDS),
    "special_requirements",
  ]);

  // Any spec fields not in any section (catch-all)
  const remainingFields = Object.keys(specs).filter(
    (k) => !coveredFields.has(k) && specs[k],
  );

  // Dimension fields that are set
  const dimFieldsSet = Array.from(DIMENSION_FIELDS).filter((f) => specs[f]);

  return (
    <Box>
      {/* ── Back + title ───────────────────────────────────── */}
      <Typography
        variant="body2"
        color="text.secondary"
        onClick={() => navigate(projectId ? `/project/${projectId}` : "/")}
        sx={{
          mb: "0.5rem",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          "&:hover": { color: PRIMARY },
        }}
      >
        <ArrowBackIcon fontSize="small" />
        {projectId ? "Back to Project" : "Back to Dashboard"}
      </Typography>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: "1.5rem",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ color: DARK, fontWeight: "bold" }}>
            Engineering Specification Sheet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {ess.project_number} — {ess.vessel_name}
          </Typography>
        </Box>

        {/* Action buttons */}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Tooltip title="Refresh">
            <Button
              variant="outlined"
              onClick={fetchEss}
              startIcon={<RefreshIcon />}
              sx={{
                borderColor: PRIMARY,
                color: PRIMARY,
                borderRadius: "8px",
                textTransform: "none",
                "&:hover": { backgroundColor: "#e6f4f4" },
              }}
            >
              Refresh
            </Button>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadWord}
            sx={{
              backgroundColor: PRIMARY,
              borderRadius: "8px",
              fontWeight: "bold",
              textTransform: "none",
              "&:hover": { backgroundColor: "#004749" },
            }}
          >
            Download Word
          </Button>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
            sx={{
              borderColor: PRIMARY,
              color: PRIMARY,
              borderRadius: "8px",
              textTransform: "none",
              "&:hover": { backgroundColor: "#e6f4f4" },
            }}
          >
            Print
          </Button>
          <Button
            variant="outlined"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            disabled={deleting}
            sx={{
              borderColor: "#b00033",
              color: "#b00033",
              borderRadius: "8px",
              textTransform: "none",
              "&:hover": { backgroundColor: "#fce8ed" },
            }}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </Box>
      </Box>

      {/* ── Error / download error banners ─────────────────── */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError("")}
          sx={{ mb: "1rem", borderRadius: "8px" }}
        >
          {error}
        </Alert>
      )}
      {dlError && (
        <Alert
          severity="error"
          onClose={() => setDlError("")}
          sx={{ mb: "1rem", borderRadius: "8px" }}
        >
          {dlError}
        </Alert>
      )}

      {/* ── Project information card ────────────────────────── */}
      <Card
        elevation={0}
        sx={{
          borderRadius: "12px",
          border: `2px solid ${color.bg}`,
          mb: "1.5rem",
        }}
      >
        <Box
          sx={{
            backgroundColor: color.bg,
            color: "white",
            padding: "0.75rem 1.25rem",
            borderRadius: "11px 11px 0 0",
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography fontWeight="bold" fontSize="0.95rem">
            📋 Project Information
          </Typography>
          <Chip
            label={compType}
            size="small"
            sx={{
              backgroundColor: "rgba(255,255,255,0.2)",
              color: "white",
              fontWeight: "bold",
            }}
          />
          {ess.tag_number && (
            <Chip
              label={ess.tag_number}
              size="small"
              sx={{
                backgroundColor: "rgba(255,255,255,0.15)",
                color: "white",
                fontSize: "0.75rem",
              }}
            />
          )}
        </Box>
        <CardContent>
          <Grid container spacing={2}>
            {[
              { label: "Project Number", value: ess.project_number },
              { label: "Customer Name", value: ess.customer_name },
              { label: "Vessel Name", value: ess.vessel_name },
              { label: "Vessel Type", value: ess.vessel_type },
              {
                label: "Classification Society",
                value: ess.classification_society,
              },
              { label: "Flag State", value: ess.flag_state },
              { label: "Voltage System", value: ess.voltage_system },
              { label: "Frequency", value: ess.frequency },
            ]
              .filter((f) => f.value)
              .map(({ label, value }) => (
                <Grid item xs={12} sm={6} md={3} key={label}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {label}
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {value}
                  </Typography>
                </Grid>
              ))}
          </Grid>
        </CardContent>
      </Card>

      {/* ── Spec sections ──────────────────────────────────── */}
      {Object.entries(sections).map(([sectionName, fields]) => (
        <SpecSectionCard
          key={sectionName}
          title={sectionName}
          fields={fields}
          specs={specs}
          color={color}
        />
      ))}

      {/* ── Additional parameters (catch-all) ──────────────── */}
      {remainingFields.length > 0 && (
        <SpecSectionCard
          title="Additional Parameters"
          fields={remainingFields}
          specs={specs}
          color={color}
        />
      )}

      {/* ── Physical envelope ──────────────────────────────── */}
      {dimFieldsSet.length > 0 && (
        <Card
          elevation={0}
          sx={{
            borderRadius: "12px",
            border: "1px solid #ffe082",
            borderLeft: "4px solid #b45309",
            mb: "1rem",
          }}
        >
          <Box
            sx={{
              backgroundColor: "#fff8e1",
              padding: "0.6rem 1.25rem",
              borderRadius: "11px 11px 0 0",
            }}
          >
            <Typography
              fontWeight="bold"
              fontSize="0.85rem"
              sx={{ color: "#b45309" }}
            >
              📐 Physical Envelope — Do Not Exceed
            </Typography>
          </Box>
          <CardContent sx={{ pt: "1rem" }}>
            <Grid container spacing={2}>
              {dimFieldsSet.map((key) => (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {formatLabel(key)}
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {String(specs[key])}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* ── Special requirements ───────────────────────────── */}
      {specs.special_requirements && (
        <>
          <Divider sx={{ mb: "1rem" }} />
          <Card
            elevation={0}
            sx={{
              borderRadius: "12px",
              border: "1px solid #e0e0e0",
              borderLeft: `4px solid ${color.bg}`,
              mb: "1.5rem",
            }}
          >
            <Box
              sx={{
                backgroundColor: color.light,
                padding: "0.6rem 1.25rem",
                borderRadius: "11px 11px 0 0",
              }}
            >
              <Typography
                fontWeight="bold"
                fontSize="0.85rem"
                sx={{ color: color.bg }}
              >
                📝 Special Requirements / Notes
              </Typography>
            </Box>
            <CardContent>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {specs.special_requirements}
              </Typography>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}

export default ViewESS;
